"use client"

import { useState, useEffect } from "react"
import { apiService } from "../../services/apiService"

const MentorManagement = () => {
  const [mentors, setMentors] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phone: "",
    country: "",
    password: "",
    confirm_password: ""
  })
  const [formErrors, setFormErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  useEffect(() => {
    console.log("Component mounted, loading mentors...")
    loadMentors()
  }, [])

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("")
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const loadMentors = async () => {
    try {
      console.log("Starting to load mentors...")
      setIsLoading(true)
      setError("")
      const response = await apiService.getMentors()
      console.log("API Response:", response)
      
      if (!Array.isArray(response)) {
        console.error("Invalid response format:", response)
        setError("Invalid response format from server")
        setMentors([])
        return
      }

      console.log("Setting mentors:", response)
      setMentors(response)
    } catch (error) {
      console.error('Error loading mentors:', error)
      setError(error.message || 'Failed to load mentors')
      setMentors([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      })
    }
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.name.trim()) {
      errors.name = "Name is required"
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required"
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid"
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required"
    }

    if (!formData.country.trim()) {
      errors.country = "Country is required"
    }

    if (!formData.password) {
      errors.password = "Password is required"
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters"
    }

    if (formData.password !== formData.confirm_password) {
      errors.confirm_password = "Passwords do not match"
    }

    return errors
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsLoading(false);
      return;
    }

    try {
      // Format the data according to the API requirements
      const mentorData = {
        username: formData.name.trim().toLowerCase().replace(/\s+/g, '_'), // Convert name to username format
        email: formData.email,
        name: formData.name,
        password: formData.password,
        confirm_password: formData.confirm_password,
        phone: formData.phone,
        country: formData.country
      };

      console.log('Submitting mentor data:', mentorData);

      await apiService.createMentor(mentorData);
      setSuccessMessage('Mentor created successfully!');
      setFormData({
        email: '',
        name: '',
        password: '',
        confirm_password: '',
        phone: '',
        country: ''
      });
      setShowForm(false);
      await loadMentors(); // Refresh the list
    } catch (error) {
      console.error('Error creating mentor:', error);
      setError(error.message || 'Failed to create mentor. Please check all fields and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteMentor = async (id) => {
    if (window.confirm("Are you sure you want to delete this mentor?")) {
      try {
        setIsLoading(true)
        const mentor = mentors.find(m => m.id === id)
        if (!mentor) {
          console.error("Mentor not found for deletion:", id)
          return
        }

        console.log("Deleting mentor:", mentor)
        await apiService.deleteMentor(id)
        
        console.log("Reloading mentors after deletion...")
        await loadMentors()
        setSuccessMessage(`Mentor "${mentor.name}" deleted successfully!`)
      } catch (error) {
        console.error("Error deleting mentor:", error)
        setError(error.message || "Failed to delete mentor")
      } finally {
        setIsLoading(false)
      }
    }
  }

  // Debug render
  console.log("Current mentors state:", mentors)
  console.log("Loading state:", isLoading)
  console.log("Error state:", error)
  console.log("Success message:", successMessage)

  return (
    <div className="mentor-management">
      <div className="action-buttons">
        <button className="btn" onClick={() => setShowForm(!showForm)} disabled={isLoading}>
          {showForm ? "Cancel" : "Add New Mentor"}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {showForm && (
        <div className="form-container">
          <h2 className="form-title">Add New Mentor</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={`form-control ${formErrors.name ? "error" : ""}`}
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    placeholder="This will be used as the username"
                  />
                  {formErrors.name && <div className="error-message">{formErrors.name}</div>}
                </div>
              </div>

              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`form-control ${formErrors.email ? "error" : ""}`}
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  {formErrors.email && <div className="error-message">{formErrors.email}</div>}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className={`form-control ${formErrors.phone ? "error" : ""}`}
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  {formErrors.phone && <div className="error-message">{formErrors.phone}</div>}
                </div>
              </div>

              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="country">Country</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    className={`form-control ${formErrors.country ? "error" : ""}`}
                    value={formData.country}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  {formErrors.country && <div className="error-message">{formErrors.country}</div>}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    className={`form-control ${formErrors.password ? "error" : ""}`}
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  {formErrors.password && <div className="error-message">{formErrors.password}</div>}
                </div>
              </div>

              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="confirm_password">Confirm Password</label>
                  <input
                    type="password"
                    id="confirm_password"
                    name="confirm_password"
                    className={`form-control ${formErrors.confirm_password ? "error" : ""}`}
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  {formErrors.confirm_password && <div className="error-message">{formErrors.confirm_password}</div>}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn" disabled={isLoading}>
                {isLoading ? "Creating..." : "Create Mentor Account"}
              </button>
            </div>
          </form>
        </div>
      )}

      <h2 className="section-title">All Mentors</h2>
      <div className="table-container">
        {isLoading ? (
          <div className="loading">Loading mentors...</div>
        ) : mentors.length === 0 ? (
          <div className="no-data">No mentors found</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Country</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {mentors.map((mentor) => (
                <tr key={mentor.id}>
                  <td>{mentor.username}</td>
                  <td>{mentor.email}</td>
                  <td>{mentor.phone}</td>
                  <td>{mentor.country}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => deleteMentor(mentor.id)}
                        disabled={isLoading}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default MentorManagement
